import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { i18n } from "../i18n";
import "./cpl-code-editor";
import "./cpl-markdown-viewer";

@customElement("cpl-markdown-editor")
export class CplMarkdownEditor extends LitElement {
  @property({ type: String }) value = "";
  @property({ type: Boolean }) readOnly = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: String }) label = "Markdown";
  @property({ type: String, attribute: "min-height" }) minHeight = "10rem";
  @state() private preview = false;
  static styles = css`
    :host {
      display: block;
      min-width: 0;
    }
    .toolbar {
      display: flex;
      gap: 4px;
      padding: 4px;
      background: #c0c0c0;
      border: 1px solid #808080;
    }
    button {
      font: inherit;
      background: #c0c0c0;
      color: #000;
      border: 2px solid;
      border-color: #fff #000 #000 #fff;
      border-radius: 0;
      padding: 2px 12px;
    }
    button[aria-pressed="true"] {
      border-color: #000 #fff #fff #000;
      background: #dfdfdf;
    }
    button:focus-visible {
      outline: 1px dotted #000;
      outline-offset: -4px;
    }
    button:disabled {
      color: #808080;
    }
    .preview {
      padding: 8px;
      border: 2px solid;
      border-color: #808080 #fff #fff #808080;
      background: #fff;
      max-height: 32rem;
      overflow: auto;
    }
    ::-webkit-scrollbar {
      width: 16px;
      height: 16px;
    }
    ::-webkit-scrollbar-track {
      background: #dfdfdf;
    }
    ::-webkit-scrollbar-thumb {
      background: #c0c0c0;
      border: 2px solid;
      border-color: #fff #808080 #808080 #fff;
    }
    [hidden] {
      display: none;
    }
  `;
  protected willUpdate(changes: PropertyValues) {
    if (changes.has("readOnly") && this.readOnly) this.preview = true;
  }
  render() {
    return html` <div class="toolbar" role="group" aria-label=${this.label}>
        <button
          type="button"
          aria-pressed=${String(!this.preview)}
          ?disabled=${this.disabled}
          @click=${() => (this.preview = false)}
        >
          ${i18n.t(this.readOnly ? "raw" : "edit")}
        </button>
        <button
          type="button"
          aria-pressed=${String(this.preview)}
          ?disabled=${this.disabled}
          @click=${() => (this.preview = true)}
        >
          ${i18n.t("preview")}
        </button>
      </div>
      <cpl-code-editor
        ?hidden=${this.preview}
        .value=${this.value}
        language="markdown"
        .readOnly=${this.readOnly}
        .disabled=${this.disabled}
        .label=${this.label}
        .minHeight=${this.minHeight}
        @value-change=${(event: CustomEvent<{ value: string }>) => {
          event.stopPropagation();
          this.value = event.detail.value;
          this.dispatchEvent(
            new CustomEvent("value-change", {
              detail: { value: this.value },
              bubbles: true,
              composed: true,
            }),
          );
        }}
      ></cpl-code-editor>
      <div class="preview" ?hidden=${!this.preview}>
        <cpl-markdown-viewer .value=${this.value}></cpl-markdown-viewer>
      </div>`;
  }
}
